'use client';

import { useEffect, useLayoutEffect, useState, useRef, useCallback, useMemo } from 'react';
import { format, isValid, isToday, isYesterday, differenceInHours } from 'date-fns';
import { RefreshCw, Paperclip, Send, X, AlertCircle, Menu, ListTree, Bot, MoreVertical, ShoppingCart } from 'lucide-react';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';
import { cn } from '@/lib/utils';
import { displayId, isInstagramId } from '@/lib/channel';
import { TemplateSelectorDialog } from '@/components/template-selector-dialog';
import { InteractiveMessageDialog } from '@/components/interactive-message-dialog';
import { CustomerProfileDialog } from '@/components/customer-profile-dialog';
import { ContactAvatar } from '@/components/contact-avatar';
import { ScopeChips, UnlinkedLineChip } from '@/components/scope-chips';
import { MessageBubble } from '@/components/messages/message-bubble';
import { MessageContent } from '@/components/messages/message-content';
import { findQuotedMessage, indexByExternalId, toUiMessage, type Message } from '@/components/messages/types';
import { ComposerBatchHint, ComposerCostLine } from '@/components/messages/composer-notes';
import { ConversationEventRow } from '@/components/conversation-event-row';
import { useAutoPolling } from '@/hooks/use-auto-polling';
import { useAdvancedVisibility } from '@/hooks/use-advanced-visibility';
import { useMessageStats } from '@/hooks/use-message-stats';
import { buildTimeline, timelineKey, timelineTimestamp, type TimelineItem } from '@/lib/conversation-timeline';
import { isLineRequiredError } from '@/lib/line-required';
import { conversationClient, type ConversationEvent, type ConversationMessage } from '@/lib/gupshup-client';
import { Button } from '@/components/ui/button';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Textarea } from '@/components/ui/textarea';
import { Skeleton } from '@/components/ui/skeleton';
import { Badge } from '@/components/ui/badge';
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';

const EVENTS_FETCH_LIMIT = 200;

function formatDateDivider(timestamp: string, t: TFunction): string {
  try {
    const date = new Date(timestamp);
    if (!isValid(date)) return '';

    if (isToday(date)) return t('messageView.today');
    if (isYesterday(date)) return t('messageView.yesterday');
    return format(date, 'MMMM d, yyyy');
  } catch {
    return '';
  }
}

function shouldShowDateDivider(currentTimestamp: string, prevTimestamp: string | null): boolean {
  if (!prevTimestamp) return true;

  try {
    const currentDate = new Date(currentTimestamp);
    const prevDate = new Date(prevTimestamp);

    if (!isValid(currentDate) || !isValid(prevDate)) return false;

    return format(currentDate, 'yyyy-MM-dd') !== format(prevDate, 'yyyy-MM-dd');
  } catch {
    return false;
  }
}

function isWithin24HourWindow(messages: Message[]): boolean {
  const inboundMessages = messages.filter(msg => msg.direction === 'inbound');

  if (inboundMessages.length === 0) {
    return false;
  }

  const lastInboundMessage = inboundMessages[inboundMessages.length - 1];

  try {
    const lastMessageDate = new Date(lastInboundMessage.createdAt);
    if (!isValid(lastMessageDate)) return false;

    const hoursSinceLastMessage = differenceInHours(new Date(), lastMessageDate);
    return hoursSinceLastMessage < 24;
  } catch {
    return false;
  }
}

function getDisabledInputMessageKey(messages: Message[]): string {
  const inboundMessages = messages.filter(msg => msg.direction === 'inbound');

  if (inboundMessages.length === 0) {
    return 'messageView.noInboundYet';
  }

  return 'messageView.outside24h';
}

type Props = {
  conversationId?: string;
  phoneNumber?: string;
  contactName?: string;
  aiDisabledUntil?: string;
  handoffUntil?: string;
  /** Aggregate sessions only: which brand/branch this chat belongs to. */
  brandId?: string;
  brandName?: string;
  locationName?: string | null;
  /** Arrived on an unlinked sucursal line — the agent does not answer there. */
  lineUnlinked?: boolean;
  onTemplateSent?: (phoneNumber: string) => Promise<void>;
  onConversationStateChanged?: (phoneNumber: string) => Promise<void>;
  onToggleSidebar?: () => void;
  /** Number of lines on the conversation's live cart. Absent when there is no draft. */
  draftItemCount?: number;
  /**
   * The brand takes orders, so the cart button stays reachable even with no draft — that is where an
   * operator starts one by hand.
   */
  canStartOrder?: boolean;
  cartOpen?: boolean;
  onToggleCart?: () => void;
  /** An order was placed for this conversation and the customer has not been notified yet. */
  hasUnsentConfirmation?: boolean;
  onOpenConfirmationSummary?: () => void;
  onDismissConfirmation?: () => void;
};

export function MessageView({
  conversationId,
  phoneNumber,
  contactName,
  aiDisabledUntil,
  handoffUntil,
  brandId,
  brandName,
  locationName,
  lineUnlinked,
  onTemplateSent,
  onConversationStateChanged,
  onToggleSidebar,
  draftItemCount,
  canStartOrder,
  cartOpen,
  onToggleCart,
  hasUnsentConfirmation,
  onOpenConfirmationSummary,
  onDismissConfirmation,
}: Props) {
  const { t } = useTranslation('conversations');
  const [messages, setMessages] = useState<Message[]>([]);
  const [events, setEvents] = useState<ConversationEvent[]>([]);
  const [showAdvanced, setShowAdvanced] = useAdvancedVisibility();
  const [loading, setLoading] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [messageInput, setMessageInput] = useState('');
  const [sending, setSending] = useState(false);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [filePreview, setFilePreview] = useState<string | null>(null);
  const [canSendRegularMessage, setCanSendRegularMessage] = useState(true);
  const [showTemplateDialog, setShowTemplateDialog] = useState(false);
  const [showInteractiveDialog, setShowInteractiveDialog] = useState(false);
  const [showProfileDialog, setShowProfileDialog] = useState(false);
  const [resumingAi, setResumingAi] = useState(false);

  const messagesEndRef = useRef<HTMLDivElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const composerRef = useRef<HTMLTextAreaElement>(null);

  const previousMessageCountRef = useRef(0);
  const lastMessageIdRef = useRef<string | null>(null);
  const hasInitializedScrollRef = useRef(false);
  const scrollViewportRef = useRef<HTMLDivElement>(null);
  const topSentinelRef = useRef<HTMLDivElement>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [loadingOlder, setLoadingOlder] = useState(false);

  // Scroll only the chat viewport. scrollIntoView would also scroll every
  // scrollable ancestor (the document on mobile), hiding the app header.
  const scrollToBottom = (instant?: boolean) => {
    const viewport = scrollViewportRef.current;
    if (!viewport) return;
    viewport.scrollTo({ top: viewport.scrollHeight, behavior: instant ? 'instant' : 'smooth' });
  };

  const transformMessages = useCallback((data: ConversationMessage[]): Message[] => {
    return data
      .map(toUiMessage)
      .sort((a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime());
  }, []);

  const isInitialFetchRef = useRef(true);
  // Tracks the conversation the view is currently showing. Any in-flight
  // request (initial fetch, poll, or older-page load) that resolves after the
  // user has switched conversations is discarded, so one conversation's
  // messages can never leak into another's list.
  const activeConversationIdRef = useRef<string | undefined>(conversationId);

  const fetchMessages = useCallback(async () => {
    if (!conversationId) return;

    try {
      // Events ride the same poll: low-volume, so one generous page is enough. A failure on the
      // events side must never take the messages fetch down with it.
      const [response, eventsResponse] = await Promise.all([
        conversationClient.messages.listByConversation({
          conversationId,
          limit: 50,
        }),
        conversationClient.events
          .listByConversation({
            conversationId,
            visibility: showAdvanced ? 'all' : 'default',
            limit: EVENTS_FETCH_LIMIT,
          })
          .catch(() => null),
      ]);

      // Stale response for a conversation we already navigated away from — drop it.
      if (conversationId !== activeConversationIdRef.current) return;

      if (eventsResponse) {
        setEvents(eventsResponse.data);
      }

      setTotalPages(response.meta.totalPages);
      const latestMessages = transformMessages(response.data);

      if (isInitialFetchRef.current) {
        // First load: replace everything
        isInitialFetchRef.current = false;
        setCurrentPage(1);
        setMessages(latestMessages);
      } else {
        // Polling: merge latest page into existing messages (preserving older pages)
        setMessages((prev) => {
          const olderMessages = prev.filter(
            (m) => !latestMessages.some((lm) => lm.id === m.id)
          );
          return [...olderMessages, ...latestMessages].sort(
            (a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime()
          );
        });
      }
    } catch (error) {
      console.error('Error fetching messages:', error);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [conversationId, transformMessages, showAdvanced]);

  const fetchOlderMessages = useCallback(async () => {
    if (!conversationId || loadingOlder || currentPage >= totalPages) return;

    const nextPage = currentPage + 1;
    setLoadingOlder(true);

    try {
      const viewport = scrollViewportRef.current;
      const previousScrollHeight = viewport?.scrollHeight ?? 0;

      const response = await conversationClient.messages.listByConversation({
        conversationId,
        page: nextPage,
        limit: 50,
      });

      // Stale response for a conversation we already navigated away from — drop it.
      if (conversationId !== activeConversationIdRef.current) return;

      const olderMessages = transformMessages(response.data);

      setMessages((prev) => {
        const existingIds = new Set(prev.map((m) => m.id));
        const newMessages = olderMessages.filter((m) => !existingIds.has(m.id));
        return [...newMessages, ...prev].sort(
          (a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime()
        );
      });

      setCurrentPage(nextPage);

      // Preserve scroll position after prepending older messages
      requestAnimationFrame(() => {
        if (viewport) {
          const newScrollHeight = viewport.scrollHeight;
          viewport.scrollTop = newScrollHeight - previousScrollHeight;
        }
      });
    } catch (error) {
      console.error('Error fetching older messages:', error);
    } finally {
      setLoadingOlder(false);
    }
  }, [conversationId, currentPage, totalPages, loadingOlder, transformMessages]);

  useEffect(() => {
    if (conversationId) {
      setLoading(true);
      fetchMessages();
    }
  }, [conversationId, fetchMessages]);

  // Reset scroll tracking and pagination when switching conversations
  useEffect(() => {
    activeConversationIdRef.current = conversationId;
    // Clear immediately so a stale in-flight response can never merge the
    // previous conversation's messages into the new one.
    setMessages([]);
    setEvents([]);
    previousMessageCountRef.current = 0;
    lastMessageIdRef.current = null;
    hasInitializedScrollRef.current = false;
    isInitialFetchRef.current = true;
    setCurrentPage(1);
    setTotalPages(1);
  }, [conversationId]);

  // IntersectionObserver to load older messages when scrolling to top
  useEffect(() => {
    const sentinel = topSentinelRef.current;
    const viewport = scrollViewportRef.current;
    if (!sentinel || !viewport || loading) return;

    // Wait a tick so the initial instant scroll-to-bottom has settled
    const timerId = setTimeout(() => {
      const observer = new IntersectionObserver(
        (entries) => {
          if (entries[0].isIntersecting) {
            fetchOlderMessages();
          }
        },
        { root: viewport, threshold: 0.1 }
      );

      observer.observe(sentinel);
      // Store cleanup in a variable the outer effect can use
      cleanupRef.current = () => observer.disconnect();
    }, 100);

    const cleanupRef = { current: () => {} };
    return () => {
      clearTimeout(timerId);
      cleanupRef.current();
    };
  }, [fetchOlderMessages, loading]);

  // Scroll behavior:
  // 1. On first load of messages for a conversation → scroll to bottom.
  // 2. On any new message (new last ID or count increased) → scroll to bottom.
  // 3. On polling with no new messages → do nothing.
  useEffect(() => {
    const currentCount = messages.length;

    if (currentCount === 0) {
      previousMessageCountRef.current = 0;
      lastMessageIdRef.current = null;
      return;
    }

    const currentLastMessageId = messages[currentCount - 1]?.id ?? null;

    // Initial scroll for this conversation — use instant to avoid race with IntersectionObserver
    if (!hasInitializedScrollRef.current) {
      hasInitializedScrollRef.current = true;
      previousMessageCountRef.current = currentCount;
      lastMessageIdRef.current = currentLastMessageId;

      setTimeout(() => {
        scrollToBottom(true);
      }, 0);

      return;
    }

    const prevLastId = lastMessageIdRef.current;

    // Only scroll to bottom when a new message appears at the end (not when older messages are prepended)
    const hasNewMessageAtEnd =
      currentLastMessageId && currentLastMessageId !== prevLastId;

    previousMessageCountRef.current = currentCount;
    lastMessageIdRef.current = currentLastMessageId;

    if (!hasNewMessageAtEnd) {
      return;
    }

    scrollToBottom();
  }, [messages]);

  useEffect(() => {
    setCanSendRegularMessage(isWithin24HourWindow(messages));
  }, [messages]);

  // Message counts for the composer's cost line. Instagram threads are never billed by Meta, and with
  // the 24 h window closed the template path replaces the composer, so neither fetches.
  const showCostLine = canSendRegularMessage && !isInstagramId(phoneNumber);
  const { stats: messageStats, recordSend: recordMessageStatsSend } = useMessageStats(conversationId, showCostLine);

  // Auto-grow the composer with its content; the CSS max-height caps it at about six lines, after
  // which it scrolls. `loading` and the window flag are deps because both remount the textarea.
  useLayoutEffect(() => {
    const el = composerRef.current;
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = `${el.scrollHeight + el.offsetHeight - el.clientHeight}px`;
  }, [messageInput, canSendRegularMessage, loading]);

  const handleRefresh = () => {
    setRefreshing(true);
    fetchMessages();
  };

  const handleResumeAi = async () => {
    if (!conversationId || resumingAi) return;
    setResumingAi(true);
    try {
      await conversationClient.conversations.resumeAi(conversationId);
      if (phoneNumber && onConversationStateChanged) {
        await onConversationStateChanged(phoneNumber);
      }
    } catch (error) {
      console.error('Error resuming AI:', error);
      toast.error(t('messageView.resumeAiError'));
    } finally {
      setResumingAi(false);
    }
  };

  // Messages with a reaction request in flight; a second tap waits for the first to settle.
  const reactingIdsRef = useRef(new Set<string>());

  // Optimistic: show our reaction at once, then take the server's copy, or put the previous one
  // back on failure. A poll landing mid-request can briefly show the old state; the reply fixes it.
  const handleReact = useCallback(
    async (message: Message, emoji: string) => {
      if (!conversationId || reactingIdsRef.current.has(message.id)) return;
      reactingIdsRef.current.add(message.id);
      const previous = message.reactions;
      const at = new Date().toISOString();
      const setReactions = (reactions: Message['reactions']) =>
        setMessages((prev) => prev.map((m) => (m.id === message.id ? { ...m, reactions } : m)));

      setReactions({ ...previous, business: emoji ? { emoji, at } : undefined });
      try {
        const response = await conversationClient.messages.react({
          conversationId,
          messageId: message.id,
          emoji,
        });
        if (conversationId !== activeConversationIdRef.current) return;
        setReactions(response.data.reactions);
      } catch (error) {
        console.error('Error sending reaction:', error);
        if (conversationId !== activeConversationIdRef.current) return;
        setMessages((prev) =>
          prev.map((m) =>
            m.id === message.id ? { ...m, reactions: { ...m.reactions, business: previous?.business } } : m,
          ),
        );
        toast.error(t('messageView.reactions.error'));
      } finally {
        reactingIdsRef.current.delete(message.id);
      }
    },
    [conversationId, t],
  );

  // Instagram senders don't support reactions, and outside the 24h window WhatsApp refuses the send.
  const canReact = !!conversationId && !isInstagramId(phoneNumber) && canSendRegularMessage;

  useAutoPolling({
    interval: 5000,
    enabled: !!conversationId,
    onPoll: fetchMessages
  });

  // Refetch (with the new visibility) whenever the toggle changes.
  useEffect(() => {
    if (!conversationId || isInitialFetchRef.current) return;
    fetchMessages();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showAdvanced]);

  // Chat timeline: messages interleaved with events by timestamp. While older message pages are
  // still unloaded, events predating the oldest loaded message are hidden so they never float
  // detached above the pagination sentinel.
  const timeline = useMemo<TimelineItem<Message>[]>(
    () =>
      buildTimeline(messages, events, {
        hideEventsBefore: currentPage >= totalPages ? null : messages[0]?.createdAt,
      }),
    [messages, events, currentPage, totalPages],
  );

  // Quote lookups ("reply to" strips) resolve against the loaded pages only; a quoted message on
  // an unloaded page shows as an unavailable quote until that page is scrolled in.
  const messagesByExternalId = useMemo(() => indexByExternalId(messages), [messages]);

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setSelectedFile(file);

    if (file.type.startsWith('image/')) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setFilePreview(reader.result as string);
      };
      reader.readAsDataURL(file);
    } else {
      setFilePreview(null);
    }
  };

  const handleRemoveFile = () => {
    setSelectedFile(null);
    setFilePreview(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleSendMessage = async (e: React.FormEvent) => {
    e.preventDefault();

    if ((!messageInput.trim() && !selectedFile) || !phoneNumber || sending) return;

    setSending(true);
    try {
      if (selectedFile) {
        toast.error(t('messageView.fileUploadNotSupported'));
        setSending(false);
        return;
      }

      if (!messageInput.trim()) {
        setSending(false);
        return;
      }

      await conversationClient.messages.sendText({
        to: phoneNumber,
        body: messageInput,
        conversationId,
      });

      recordMessageStatsSend();
      setMessageInput('');
      handleRemoveFile();
      await fetchMessages();
    } catch (error) {
      console.error('Error sending message:', error);
      // Sending needs a line (404 LINE_REQUIRED, K5): say what is missing instead of a
      // generic failure the operator can do nothing about.
      toast.error(isLineRequiredError(error) ? t('app:lineRequired.description') : t('messageView.sendError'));
    } finally {
      setSending(false);
    }
  };

  // Enter sends and Shift+Enter breaks the line. Never send mid IME composition: there Enter commits
  // the composed text (keyCode 229 covers browsers that end the composition before keydown).
  const handleComposerKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key !== 'Enter' || e.shiftKey || e.nativeEvent.isComposing || e.keyCode === 229) return;
    // Touch keyboards have no Shift+Enter: there Enter adds a line and the send button sends.
    if (window.matchMedia?.('(pointer: coarse)').matches) return;
    e.preventDefault();
    e.currentTarget.form?.requestSubmit();
  };

  const handleInteractiveSent = async () => {
    recordMessageStatsSend();
    await fetchMessages();
  };

  const handleTemplateSent = async () => {
    await fetchMessages();

    if (phoneNumber && onTemplateSent) {
      await onTemplateSent(phoneNumber);
    }
  };

  if (!conversationId) {
    return (
      <div className="flex-1 min-w-0 flex flex-col bg-chat-panel/50">
        <div className="p-3 border-b border-chat-border bg-chat-panel md:hidden">
          <div className="flex items-center">
            {onToggleSidebar && (
              <Button
                onClick={onToggleSidebar}
                variant="ghost"
                size="icon"
                aria-label={t('messageView.openMenuAria')}
                className="text-chat-text-muted hover:bg-chat-border/30"
              >
                <Menu className="h-5 w-5" />
              </Button>
            )}
            <h2 className="text-base font-medium text-chat-text ml-2">{t('messageView.title')}</h2>
          </div>
        </div>
        <div className="flex-1 flex items-center justify-center">
          <p className="text-chat-text-muted">{t('messageView.selectConversation')}</p>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex-1 min-w-0 flex flex-col bg-chat-wallpaper">
        <div className="p-3 border-b border-chat-border bg-chat-panel">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3 flex-1 min-w-0">
              {onToggleSidebar && (
                <Button
                  onClick={onToggleSidebar}
                  variant="ghost"
                  size="icon"
                  aria-label={t('messageView.openMenuAria')}
                  className="md:hidden text-chat-text-muted hover:bg-chat-border/30"
                >
                  <Menu className="h-5 w-5" />
                </Button>
              )}
              <div className="flex-1">
                <Skeleton className="h-5 w-40 mb-1" />
                <Skeleton className="h-3 w-32" />
              </div>
            </div>
            <Skeleton className="h-9 w-24 rounded-lg" />
          </div>
        </div>
        <div className="flex-1 overflow-y-auto p-4">
          <div className="max-w-[900px] mx-auto space-y-3">
            {[1, 2, 3, 4, 5, 6].map((i) => (
              <div key={i} className={cn('flex mb-2', i % 2 === 0 ? 'justify-end' : 'justify-start')}>
                <div
                  className={cn(
                    'max-w-[70%] rounded-lg px-3 py-2 shadow-sm',
                    i % 2 === 0 ? 'rounded-br-none' : 'rounded-bl-none'
                  )}
                >
                  <Skeleton className={cn('h-4 mb-2', ['w-40', 'w-52', 'w-36'][i % 3])} />
                  <Skeleton className="h-3 w-16" />
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    // min-w-0: this is a flex item, so without it the pane is sized by its *content's* min width and
    // grows past the viewport on mobile — the header actions and the right half of every bubble end
    // up clipped outside the screen.
    <div className="flex-1 min-w-0 flex flex-col bg-chat-wallpaper">
      <div className="p-3 border-b border-chat-border bg-chat-panel">
        <div className="flex items-center justify-between gap-2">
          <div className="flex items-center gap-3 min-w-0">
            {onToggleSidebar && (
              <Button
                onClick={onToggleSidebar}
                variant="ghost"
                size="icon"
                className="md:hidden text-chat-text-muted hover:bg-chat-border/30"
              >
                <Menu className="h-5 w-5" />
              </Button>
            )}
            <button
              type="button"
              className="group flex min-w-0 items-center gap-3 text-left hover:opacity-80 transition-opacity"
              onClick={() => phoneNumber && setShowProfileDialog(true)}
              title="Ver perfil del cliente"
            >
              <ContactAvatar
                contactName={contactName}
                phoneNumber={phoneNumber}
                className="h-9 w-9 flex-shrink-0"
                textClassName="text-xs"
              />
              <div className="min-w-0">
                <div className="flex items-center gap-2 min-w-0">
                  <h2 className="truncate text-base font-medium text-chat-text">
                    {contactName || (phoneNumber && displayId(phoneNumber)) || t('messageView.conversationFallback')}
                  </h2>
                  {isInstagramId(phoneNumber) && (
                    <span className="inline-flex shrink-0 items-center px-2 py-0.5 rounded-full text-[11px] font-medium bg-pink-100 dark:bg-pink-900/40 text-pink-700 dark:text-pink-300">
                      Instagram
                    </span>
                  )}
                  {aiDisabledUntil && new Date(aiDisabledUntil) > new Date() && (
                    <span className="inline-flex shrink-0 items-center px-2 py-0.5 rounded-full text-[11px] font-medium bg-orange-100 dark:bg-orange-900/40 text-orange-700 dark:text-orange-300">
                      {t('messageView.aiOffUntil', { time: format(new Date(aiDisabledUntil), 'HH:mm') })}
                    </span>
                  )}
                  {handoffUntil && new Date(handoffUntil) > new Date() && (
                    <span className="inline-flex shrink-0 items-center px-2 py-0.5 rounded-full text-[11px] font-medium bg-red-100 dark:bg-red-900/40 text-red-700 dark:text-red-300">
                      {t('messageView.handoffRequested')}
                    </span>
                  )}
                  {lineUnlinked && (
                    <UnlinkedLineChip label={t('messageView.lineUnlinked')} className="shrink-0" />
                  )}
                  <ScopeChips brandName={brandName} locationName={locationName} surface="chat" />
                </div>
                {phoneNumber && (
                  <p className="truncate text-xs text-chat-text-muted">
                    {contactName ? `${displayId(phoneNumber)} · ` : ''}
                    <span className="text-chat-accent-deep group-hover:underline">
                      Ver perfil
                    </span>
                  </p>
                )}
              </div>
            </button>
          </div>
          <div className="flex shrink-0 items-center gap-1">
            {/* Two states in one button, because they are two very different things. A live cart is a
                customer mid-order and gets the loud treatment — filled, counted, impossible to walk
                past. With no cart it is a quiet way in to taking one by hand, and must not compete
                with the conversation for attention. */}
            {(draftItemCount !== undefined || canStartOrder) && onToggleCart && (
              <Button
                onClick={onToggleCart}
                variant={draftItemCount !== undefined ? 'default' : 'ghost'}
                size="sm"
                aria-pressed={cartOpen}
                className={cn(
                  'gap-1.5',
                  draftItemCount !== undefined
                    ? 'bg-chat-accent-deep text-white shadow-sm hover:bg-chat-accent-deep-hover'
                    : 'text-chat-text-muted hover:bg-chat-panel',
                )}
                title={draftItemCount !== undefined ? t('cart.openTooltip') : t('cart.start.tooltip')}
              >
                <ShoppingCart className="h-4 w-4" />
                <span className="hidden sm:inline">
                  {draftItemCount !== undefined ? t('cart.openActive') : t('cart.start.action')}
                </span>
                {draftItemCount !== undefined && draftItemCount > 0 && (
                  <span className="rounded-full bg-white/25 px-1.5 text-[10px] font-semibold leading-4 text-white">
                    {draftItemCount}
                  </span>
                )}
              </Button>
            )}
            {((aiDisabledUntil && new Date(aiDisabledUntil) > new Date()) ||
              (handoffUntil && new Date(handoffUntil) > new Date())) && (
              <Button
                onClick={handleResumeAi}
                loading={resumingAi}
                variant="outline"
                size="sm"
                className="text-chat-accent-deep border-chat-accent-deep hover:bg-chat-accent-deep/5 gap-1.5"
                title={t('messageView.resumeAiTooltip')}
              >
                {!resumingAi && <Bot className="h-4 w-4" />}
                <span className="hidden sm:inline">{t('messageView.resumeAi')}</span>
              </Button>
            )}
            <Button
              onClick={handleRefresh}
              disabled={refreshing}
              variant="ghost"
              size="icon"
              aria-label={t('messageView.refreshAria')}
              className="text-chat-text-muted hover:bg-chat-panel"
            >
              <RefreshCw className={cn('h-4 w-4', refreshing && 'animate-spin')} />
            </Button>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="ghost"
                  size="icon"
                  aria-label={t('messageView.optionsMenuAria')}
                  className="text-chat-text-muted hover:bg-chat-panel"
                >
                  <MoreVertical className="h-4 w-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuCheckboxItem
                  checked={showAdvanced}
                  onCheckedChange={setShowAdvanced}
                >
                  {t('messageView.advancedVisibility')}
                </DropdownMenuCheckboxItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      </div>

      {/*
          The order is in the POS and its payment link is already counting down, but the customer has
          not been told anything — the agent is paused, so nothing else will tell them. Deliberately
          persistent rather than a toast: it has to still be here after the operator closes the
          summary, looks at another conversation, and comes back.
      */}
      {hasUnsentConfirmation && (
        <div className="flex shrink-0 items-center gap-2 border-b border-amber-200 bg-amber-50 px-4 py-2 text-xs text-amber-800 dark:border-amber-800/60 dark:bg-amber-950/40 dark:text-amber-300">
          <AlertCircle className="h-3.5 w-3.5 shrink-0" />
          <span className="flex-1">{t('cart.confirmed.notNotified')}</span>
          <Button variant="ghost" size="sm" className="h-7 text-xs" onClick={onOpenConfirmationSummary}>
            {t('cart.confirmed.sendNow')}
          </Button>
          <button
            type="button"
            onClick={onDismissConfirmation}
            aria-label={t('cart.confirmed.dismiss')}
            className="shrink-0 rounded p-0.5 hover:bg-amber-100 dark:hover:bg-amber-900/40"
          >
            <X className="h-3.5 w-3.5" />
          </button>
        </div>
      )}

      <ScrollArea className="flex-1 h-0 p-4" viewportRef={scrollViewportRef}>
        <div className="max-w-[900px] mx-auto">
          {/* Sentinel element for loading older messages */}
          <div ref={topSentinelRef} className="h-1" />
          {loadingOlder && (
            <div className="flex justify-center py-3">
              <RefreshCw className="h-4 w-4 animate-spin text-chat-text-muted" />
            </div>
          )}
          {currentPage >= totalPages && messages.length > 0 && totalPages > 1 && (
            <p className="text-center text-xs text-chat-text-muted py-2">
              {t('messageView.beginningOfConversation')}
            </p>
          )}
          {timeline.length === 0 ? (
            <p className="text-center text-chat-text-muted">{t('messageView.noMessages')}</p>
          ) : (
            timeline.map((item, index) => {
              const prevItem = index > 0 ? timeline[index - 1] : null;
              const showDateDivider = shouldShowDateDivider(
                timelineTimestamp(item),
                prevItem ? timelineTimestamp(prevItem) : null
              );
              return (
                <div key={timelineKey(item)} className="animate-in fade-in slide-in-from-bottom-2 duration-200">
                  {showDateDivider && (
                    <div className="flex justify-center my-4">
                      <Badge variant="secondary" className="shadow-sm">
                        {formatDateDivider(timelineTimestamp(item), t)}
                      </Badge>
                    </div>
                  )}

                  {item.kind === 'message' ? (
                    <MessageBubble
                      message={item.message}
                      quoted={
                        item.message.replyTo ? findQuotedMessage(item.message, messagesByExternalId) : undefined
                      }
                      onReact={
                        canReact && !item.message.fromReaction
                          ? (emoji) => handleReact(item.message, emoji)
                          : undefined
                      }
                    >
                      <MessageContent message={item.message} />
                    </MessageBubble>
                  ) : (
                    <ConversationEventRow event={item.event} />
                  )}
                </div>
              );
            })
          )}
          <div ref={messagesEndRef} />
        </div>
      </ScrollArea>

      <div className="border-t border-chat-border bg-chat-panel">
        {canSendRegularMessage ? (
          <>
            {selectedFile && (
              <div className="p-3 border-b border-chat-border bg-chat-surface">
                <div className="flex items-start gap-3">
                  {filePreview ? (
                    <img
                      src={filePreview}
                      alt={t('messageView.previewAlt')}
                      className="w-16 h-16 object-cover rounded"
                    />
                  ) : (
                    <div className="w-16 h-16 bg-chat-panel rounded flex items-center justify-center">
                      <Paperclip className="h-6 w-6 text-chat-text-muted" />
                    </div>
                  )}
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-chat-text truncate">
                      {selectedFile.name}
                    </p>
                    <p className="text-xs text-chat-text-muted">
                      {(selectedFile.size / 1024).toFixed(1)} KB
                    </p>
                  </div>
                  <Button
                    onClick={handleRemoveFile}
                    type="button"
                    variant="ghost"
                    size="icon"
                    aria-label={t('messageView.removeFileAria')}
                    className="text-chat-text-muted"
                  >
                    <X className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            )}

            <form
              onSubmit={handleSendMessage}
              className="p-3 max-w-[900px] mx-auto w-full flex gap-2 items-end"
            >
              <input
                ref={fileInputRef}
                type="file"
                onChange={handleFileSelect}
                accept="image/*,video/*,audio/*,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
                className="hidden"
              />
              {/* TODO: Add file upload button */}
              <Button
                type="button"
                onClick={() => setShowInteractiveDialog(true)}
                disabled={sending}
                size="icon"
                variant="ghost"
                aria-label={t('messageView.sendInteractive')}
                className="text-chat-text-muted hover:text-chat-accent hover:bg-chat-panel"
                title={t('messageView.sendInteractive')}
              >
                <ListTree className="h-5 w-5" />
              </Button>
              <Textarea
                ref={composerRef}
                rows={1}
                value={messageInput}
                onChange={(e) => setMessageInput(e.target.value)}
                onKeyDown={handleComposerKeyDown}
                placeholder={t('messageView.inputPlaceholder')}
                disabled={sending}
                className="flex-1 min-h-9 max-h-[134px] field-sizing-fixed resize-none overflow-y-auto py-1.5 leading-5 bg-chat-elevated dark:bg-chat-elevated border-chat-border focus-visible:ring-chat-accent rounded-lg"
              />
              <Button
                type="submit"
                loading={sending}
                disabled={!messageInput.trim() && !selectedFile}
                size="icon"
                aria-label={t('messageView.sendAria')}
                className="bg-chat-accent hover:bg-chat-accent-hover rounded-full"
              >
                {!sending && <Send className="h-5 w-5" />}
              </Button>
            </form>
            {showCostLine && (
              <div className="px-3 pb-2 -mt-1 max-w-[900px] mx-auto w-full space-y-0.5">
                <ComposerBatchHint messages={messages} draft={messageInput} />
                <ComposerCostLine stats={messageStats} />
              </div>
            )}
          </>
        ) : (
          <div className="p-3 max-w-[900px] mx-auto w-full">
            <div className="bg-chat-notice-bg border border-chat-notice-border rounded-lg p-4">
              <div className="flex items-start gap-3">
                <AlertCircle className="h-5 w-5 text-chat-notice-text flex-shrink-0 mt-0.5" />
                <div className="flex-1 min-w-0">
                  <p className="text-sm text-chat-text mb-3">
                    {isInstagramId(phoneNumber)
                      ? t('messageView.instagramWindowClosed')
                      : t(getDisabledInputMessageKey(messages))}
                  </p>
                  {!isInstagramId(phoneNumber) && (
                    <Button
                      onClick={() => setShowTemplateDialog(true)}
                      className="bg-chat-accent hover:bg-chat-accent-hover"
                      size="sm"
                    >
                      <Send className="h-4 w-4 mr-2" />
                      {t('messageView.sendTemplate')}
                    </Button>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      <TemplateSelectorDialog
        open={showTemplateDialog}
        onOpenChange={setShowTemplateDialog}
        phoneNumber={phoneNumber || ''}
        conversationId={conversationId}
        onTemplateSent={handleTemplateSent}
      />

      <InteractiveMessageDialog
        open={showInteractiveDialog}
        onOpenChange={setShowInteractiveDialog}
        conversationId={conversationId}
        phoneNumber={phoneNumber}
        onMessageSent={handleInteractiveSent}
      />

      {phoneNumber && (
        <CustomerProfileDialog
          open={showProfileDialog}
          onOpenChange={setShowProfileDialog}
          phoneNumber={phoneNumber}
          contactName={contactName}
          brandId={brandId}
        />
      )}
    </div>
  );
}
